import { useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";

import { UserRoles, UserStatus } from "../../types/user.types";
import { ReactComponent as ISOLogoIcon } from "../../assets/isotipo.svg";
import { useSidebar } from "../../context/SidebarContext";
import { dashboardConfigs } from "./dashboard-config";
import { LogoutButton } from "./logout-button";
import { useAuth } from "../../context/AuthContext";
import { TopBar } from "../../components/top-bar/TopBar";
import { NotificationsDropdown } from "../../components/top-bar/NotificationsDropdown";
import { useUserNotifications } from "../../context/UserNotificationsContext";
import { useCallback, useEffect, useState } from "react";
import { Bell, KeyIcon, Menu, X } from "lucide-react";

interface NavigationItem {
  name: string;
  href: string;
  current: boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}
interface DashboardMenuItems {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}
export type DashboardConfig = {
  [userRole: number]: {
    active?: DashboardMenuItems[];
    registered?: DashboardMenuItems[];
  };
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const [navigation, setNavigation] = useState<NavigationItem[]>([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const { unreadCount } = useUserNotifications();

  const getNavigation = useCallback(() => {
    if (!user) {
      navigate("/");
      return [];
    }

    const roleConfig = dashboardConfigs[user.roleId];
    if (!roleConfig) return [];

    const userStatusConfig =
      user.status === UserStatus.REGISTERED
        ? roleConfig.registered
        : roleConfig.active;

    if (!userStatusConfig) return [];

    return userStatusConfig
      .map((item) => ({
        ...item,
        current: location.pathname === item.href,
      }))
      .filter((item) => {
        if (user.roleId === 3) {
          if (!user.teamId) {
            if (user.status === UserStatus.REGISTERED) {
              return true;
            }

            return ["Inicio", "Crear Equipo", "Ayuda"].includes(item.name);
          }
          return item.name !== "Crear Equipo";
        }
        return true;
      });
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (user) {
      setNavigation(getNavigation());
    }
  }, [user, getNavigation]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Cerrar menú lateral</span>
                  <X aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
              <div className="flex h-16 shrink-0 items-center space-x-4">
                <ISOLogoIcon className="w-14 " />
              </div>

              <nav className="flex flex-1 flex-col">
                <ul className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul className="-mx-2 space-y-1">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={classNames(
                              item.current
                                ? "bg-gray-50 text-lime-600"
                                : "text-gray-700 hover:bg-gray-50 hover:text-lime-600",
                              "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                            )}
                          >
                            <item.icon
                              aria-hidden="true"
                              className={classNames(
                                item.current
                                  ? "text-lime-600"
                                  : "text-gray-400 group-hover:text-lime-600",
                                "h-6 w-6 shrink-0"
                              )}
                            />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="-mx-6 mt-auto">
                    <LogoutButton />
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
          <div className="flex h-16 shrink-0 items-center space-x-4">
            <ISOLogoIcon className="w-14 " />
          </div>

          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className={classNames(
                          item.current
                            ? "bg-gray-50 text-lime-600"
                            : "text-gray-700 hover:bg-gray-50 hover:text-lime-600",
                          "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                        )}
                      >
                        <item.icon
                          aria-hidden="true"
                          className={classNames(
                            item.current
                              ? "text-lime-600"
                              : "text-gray-400 group-hover:text-lime-600",
                            "h-6 w-6 shrink-0"
                          )}
                        />
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>

              <li className="-mx-6 mt-auto">
                <LogoutButton />
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="sticky top-0 z-40 flex items-center gap-x-4 bg-white px-4 py-3 shadow-sm sm:px-6 lg:hidden">
        <div className="flex lg:hidden">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 focus:outline-none focus:ring-0"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 text-sm/6 font-semibold text-gray-900">Menú</div>
        <div className="relative" ref={notificationsRef}>
          {(user?.roleId === UserRoles.CLIENT_ADMIN ||
            user?.roleId === UserRoles.SYSTEM_ADMIN) && (
            <button
              onClick={() => navigate("/dashboard/me/reset-password")}
              className="p-1 md:p-2 rounded-full hover:bg-gray-100"
            >
              <KeyIcon className="h-5 w-5 md:h-6 md:w-6 text-gray-600" />
            </button>
          )}
          <button
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
          >
            <Bell className="h-6 w-6 md:h-7 md:w-7 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
          {isNotificationsOpen && (
            <NotificationsDropdown
              onClose={() => setIsNotificationsOpen(false)}
              isMobile={true}
            />
          )}
        </div>
      </div>

      <div className="flex">
        <main className="lg:pl-72 flex-1">
          <div className="hidden lg:block">
            <TopBar />
          </div>
          <div className="px-0">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
};
